import moment from 'moment';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { useGetRestaurants } from '@features/tables/hooks/useGetRestaurants';
import { useGetTablesAvailability } from '@features/tables/hooks/useGetTablesAvailability';

import { TableForm } from '../../../components/TableForm/TableForm';
import { DiningOption as DiningOptionType, PackagesFormValues } from '../../../types';
import { filterTimeslotsByMenuOrTimeRestriction } from '../helpers/filterTimeslotsByMenuOrTimeRestrictions';
import { getPromotions } from '../helpers/getPromotions';
import { DiningOptionWrapper } from './DiningOption.styles';
import { usePackageConfiguration } from '../../../contexts';
import { formatDateISO } from '../../../helpers/dateFormatters';

type DiningOptionProps = {
  option: DiningOptionType;
  optionIndex: number;
};

export const DiningOption = ({ option, optionIndex }: DiningOptionProps) => {
  const { property, language, journeyParams } = usePackageConfiguration();

  const { data } = useGetRestaurants(property.id, language);
  const restaurants = data?.filter((r) => option.id === r.config.id);
  const { watch, setValue } = useFormContext<PackagesFormValues, any>();

  const adults = watch(`tables.restaurants.${optionIndex}.adults`) || journeyParams.adults;
  const children = watch(`tables.restaurants.${optionIndex}.children`) || journeyParams.children;
  const visitDate = watch(`tables.restaurants.${optionIndex}.visitDate`);
  const restaurant = watch(`tables.restaurants.${optionIndex}.restaurant`);
  const selectedTimeslot = watch(`tables.restaurants.${optionIndex}.timeslot`);

  const { data: timeslotsData, isFetching: isFetchingTimeslots } = useGetTablesAvailability(
    property.id,
    formatDateISO(moment(visitDate)),
    restaurant?.restaurantName || '',
    restaurant?.id || 0,
    adults,
    children ?? 0
  );

  useEffect(() => {
    if (restaurants?.length === 1 && !restaurant) {
      const { restaurantName, config } = restaurants[0];
      setValue(`tables.restaurants.${optionIndex}.restaurant`, {
        restaurantName,
        configName: config.name,
        id: config.id,
      });
      setValue(`tables.restaurants.${optionIndex}.dayNumber`, option.dayNumber);
    }
  }, [restaurants, optionIndex, restaurant, setValue, option.dayNumber]);

  const { timeSlots: timeslots, promotions: promotionsData } = timeslotsData || {
    timeSlots: [],
    promotions: [],
  };

  const filteredTimeSlots = filterTimeslotsByMenuOrTimeRestriction(timeslots, option);

  const promotions = getPromotions(promotionsData, selectedTimeslot as any, option.menuIds);

  return (
    <DiningOptionWrapper>
      <TableForm
        restaurants={restaurants ?? []}
        timeslots={filteredTimeSlots}
        promotions={promotions}
        isFetching={isFetchingTimeslots}
        optionIndex={optionIndex}
        option={option}
        hasMenuRestrictions={option.menuIds.length === 1}
      />
    </DiningOptionWrapper>
  );
};
